import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { 
  ArrowLeft, 
  FileCheck,
  CheckCircle,
  XCircle,
  Clock,
  User,
  Phone,
  Mail,
  CreditCard
} from 'lucide-react';
import { toast } from 'sonner';

export default function AdminClaims() {
  const [loading, setLoading] = useState(true);
  const [claims, setClaims] = useState([]);

  const fetchClaims = async () => {
    try {
      const data = await api.admin.getClaims();
      setClaims(data);
    } catch (error) {
      console.error('Error fetching claims:', error);
      toast.error('Error al cargar reclamos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClaims();
  }, []);

  const handleApprove = async (claimId) => {
    if (!window.confirm('¿Aprobar este reclamo y procesar el pago?')) return;
    
    try {
      await api.admin.approveClaim(claimId);
      toast.success('Reclamo aprobado');
      fetchClaims();
    } catch (error) {
      toast.error('Error al aprobar reclamo');
    }
  };

  const handleReject = async (claimId) => {
    if (!window.confirm('¿Rechazar este reclamo?')) return;
    
    try {
      await api.admin.rejectClaim(claimId);
      toast.success('Reclamo rechazado');
      fetchClaims();
    } catch (error) {
      toast.error('Error al rechazar reclamo');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending_verification: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Verificando' },
      pending_document: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Esperando documento' },
      pending_video: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Esperando video' },
      pending_approval: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Pendiente aprobación' },
      approved: { bg: 'bg-success/10', text: 'text-success', label: 'Aprobado' },
      rejected: { bg: 'bg-destructive/10', text: 'text-destructive', label: 'Rechazado' },
    };
    
    const style = styles[status] || styles.pending_verification;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
        {style.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-CO', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="page-container pt-6 space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-40 w-full rounded-2xl" />
        <Skeleton className="h-40 w-full rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="page-container pt-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/admin">
          <Button variant="ghost" size="icon" className="rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        </Link>
        <h1 className="heading-2">Reclamos de Premio</h1>
      </div>

      {/* Claims List */}
      {claims.length === 0 ? (
        <Card className="card-base text-center py-12">
          <FileCheck className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
          <h2 className="heading-4 mb-2">No hay reclamos</h2>
          <p className="text-muted-foreground">
            Los reclamos de premio aparecerán aquí
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {claims.map((claim) => (
            <Card key={claim.id} className="card-base" data-testid={`claim-card-${claim.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <User className="w-5 h-5" />
                    {claim.first_name} {claim.last_name}
                  </CardTitle>
                  {getStatusBadge(claim.status)}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-muted-foreground" />
                    <span>{claim.document_number}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <span>{claim.phone}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <span>{claim.email}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Clock className="w-4 h-4" />
                  {formatDate(claim.created_at)}
                </div>

                {claim.status === 'pending_approval' && (
                  <div className="flex gap-3 pt-2">
                    <Button
                      onClick={() => handleApprove(claim.id)}
                      className="flex-1 bg-success hover:bg-success/90 rounded-full"
                      data-testid={`approve-claim-${claim.id}`}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Aprobar
                    </Button>
                    <Button
                      onClick={() => handleReject(claim.id)}
                      variant="outline"
                      className="flex-1 rounded-full text-destructive border-destructive/20"
                      data-testid={`reject-claim-${claim.id}`}
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Rechazar
                    </Button>
                  </div>
                )}

                {claim.status === 'approved' && (
                  <div className="flex items-center gap-2 p-3 bg-success/10 rounded-xl text-success">
                    <CheckCircle className="w-5 h-5" />
                    <span className="text-sm font-medium">Premio pagado</span>
                  </div>
                )}

                {claim.status === 'rejected' && (
                  <div className="flex items-center gap-2 p-3 bg-destructive/10 rounded-xl text-destructive">
                    <XCircle className="w-5 h-5" />
                    <span className="text-sm font-medium">Reclamo rechazado</span>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
